import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Filter, Calendar, ChevronDown } from "lucide-react";
import "./HomeWorldCupFixture.css";

const MONTHS_ES = ["ENERO","FEBRERO","MARZO","ABRIL","MAYO","JUNIO","JULIO","AGOSTO","SEPTIEMBRE","OCTUBRE","NOVIEMBRE","DICIEMBRE"];

function fmtDayMonth(dateStr) {
  if (!dateStr) return "";
  const [y, m, d] = dateStr.split("-").map(Number);
  return `${d} DE ${MONTHS_ES[m - 1] || ""}`;
}

const STAGES = [
  { k: "all", label: "Todos los partidos" },
  { k: "Fase de grupos", label: "Fase de grupos" },
  { k: "Dieciseisavos", label: "Dieciseisavos" },
  { k: "Octavos", label: "Octavos" },
  { k: "Cuartos", label: "Cuartos" },
  { k: "Semifinal", label: "Semifinal" },
  { k: "Final", label: "Final" },
];

export default function HomeWorldCupFixture({ compact = false }) {
  const [matches, setMatches] = useState([]);
  const [enabled, setEnabled] = useState(true);
  const [stage, setStage] = useState("all");
  const [openMenu, setOpenMenu] = useState(false);
  const [page, setPage] = useState(0);
  const perPage = compact ? 3 : 4;

  useEffect(() => {
    api.get("/worldcup").then((r) => {
      setMatches(r.data?.matches || []);
      setEnabled(!!r.data?.enabled);
    }).catch(() => {});
  }, []);

  const filtered = useMemo(() => {
    if (stage === "all") return matches;
    return matches.filter((m) => m.stage === stage);
  }, [matches, stage]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / perPage));
  const safePage = Math.min(page, pageCount - 1);
  const visible = filtered.slice(safePage * perPage, (safePage + 1) * perPage);

  if (!enabled || matches.length === 0) return null;

  // Last finished match (most recent)
  const lastFinished = [...matches]
    .filter((m) => m.status === "finished" && m.score_home !== null && m.score_home !== undefined)
    .sort((a, b) => (b.date || "").localeCompare(a.date || "") || (b.time_bol || "").localeCompare(a.time_bol || ""))
    [0];

  return (
    <section className={`hwc-mod ${compact ? "hwc-compact" : ""}`} data-testid="home-wc-fixture">
      {/* Last result */}
      {lastFinished && (
        <div className="hwc-last" data-testid="hwc-last-result">
          <p className="hwc-last-label">ÚLTIMO RESULTADO</p>
          <div className="hwc-last-row">
            <div className="hwc-last-team">
              <img src={`https://flagcdn.com/w160/${lastFinished.home_flag}.png`} alt={lastFinished.home} />
              <span>{lastFinished.home}</span>
            </div>
            <div className="hwc-last-score">{lastFinished.score_home} - {lastFinished.score_away}</div>
            <div className="hwc-last-team hwc-last-team-right">
              <span>{lastFinished.away}</span>
              <img src={`https://flagcdn.com/w160/${lastFinished.away_flag}.png`} alt={lastFinished.away} />
            </div>
          </div>
          <p className="hwc-last-meta">{fmtDayMonth(lastFinished.date)} · FINAL</p>
        </div>
      )}

      {/* Header */}
      <div className="hwc-head">
        <div>
          <h2 className="hwc-title">FIXTURE DEL MUNDIAL</h2>
          <div className="hwc-title-bar" />
        </div>
        <div className="hwc-ball" aria-hidden>
          <span className="hwc-ball-inner" />
        </div>
      </div>

      {/* Filter dropdown */}
      <div className="hwc-filter">
        <button
          type="button"
          onClick={() => setOpenMenu((o) => !o)}
          className="hwc-filter-btn"
          data-testid="hwc-filter-toggle"
          aria-expanded={openMenu}
        >
          <Filter size={16} />
          <span>{(STAGES.find((s) => s.k === stage)?.label || "Todos los partidos").toUpperCase()}</span>
          <ChevronDown size={18} className={openMenu ? "hwc-rot" : ""} />
        </button>
        {openMenu && (
          <ul className="hwc-filter-menu" role="menu">
            {STAGES.map((s) => (
              <li key={s.k}>
                <button
                  type="button"
                  onClick={() => { setStage(s.k); setPage(0); setOpenMenu(false); }}
                  className={`hwc-filter-opt ${stage === s.k ? "hwc-filter-opt-active" : ""}`}
                  data-testid={`hwc-stage-${s.k}`}
                >
                  {s.label}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* CTA */}
      <Link to="/categoria/deportes" className="hwc-cta" data-testid="hwc-cta">
        <Calendar size={18} />
        <span>FIXTURE COMPLETO</span>
      </Link>

      {/* Matches */}
      <div className="hwc-list">
        {visible.length === 0 && <p className="hwc-empty">No hay partidos en esta etapa.</p>}
        {visible.map((m) => (
          <article key={m.id} className="hwc-match" data-testid={`hwc-match-${m.id}`}>
            <div className="hwc-match-meta">
              <span className="hwc-date">{fmtDayMonth(m.date)}</span>
              <span className="hwc-sep">|</span>
              <span className="hwc-time">{m.time_bol}</span>
              <span className="hwc-stage">{(m.stage || "").toUpperCase()}</span>
            </div>
            <div className="hwc-teams">
              <div className="hwc-team hwc-team-left">
                <img src={`https://flagcdn.com/w320/${m.home_flag}.png`} alt={m.home} loading="lazy" />
                <p>{m.home}</p>
              </div>
              <div className="hwc-vs">VS</div>
              <div className="hwc-team hwc-team-right">
                <p>{m.away}</p>
                <img src={`https://flagcdn.com/w320/${m.away_flag}.png`} alt={m.away} loading="lazy" />
              </div>
            </div>
            <div className="hwc-glow-line" />
          </article>
        ))}
      </div>

      {/* Pager */}
      <div className="hwc-pager">
        <button
          type="button"
          onClick={() => setPage(Math.max(0, safePage - 1))}
          disabled={safePage === 0}
          className="hwc-pager-btn hwc-pager-prev"
          data-testid="hwc-pager-prev"
        >
          ‹ Anterior
        </button>
        <span className="hwc-pager-info">{safePage + 1} / {pageCount}</span>
        <button
          type="button"
          onClick={() => setPage(Math.min(pageCount - 1, safePage + 1))}
          disabled={safePage >= pageCount - 1}
          className="hwc-pager-btn hwc-pager-next"
          data-testid="hwc-pager-next"
        >
          Siguiente ›
        </button>
      </div>
    </section>
  );
}
